import { useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { HomeworkAssignmentSnapshot } from '@wx-weiqi/contracts';

import {
  rankHomeworkAssignmentPerformances,
  summarizeHomeworkAssignmentPerformance,
  type HomeworkAssignmentPerformance,
} from '../domain/homework-grading';
import { formatHomeworkElapsedTime } from '../domain/homework-review-presentation';
import type { HomeworkStore } from '../stores/homework-store';
import type { TeachingStore } from '../stores/teaching-store';
import './teacher-data-dialogs.css';

export type HomeworkAnalyticsMode = 'HOMEWORK_DATA' | 'HOMEWORK_STUDENT_RESULTS';

export function filterHomeworkAssignmentsByClass(
  assignments: readonly HomeworkAssignmentSnapshot[],
  classNames: ReadonlyMap<string, string>,
  query: string,
): readonly HomeworkAssignmentSnapshot[] {
  const search = query.trim().toLocaleLowerCase();
  return search === ''
    ? assignments
    : assignments.filter(
        (assignment) =>
          classNames.get(assignment.classId)?.toLocaleLowerCase().includes(search) === true,
      );
}

function elapsedLabel(elapsedMs: number): string {
  return formatHomeworkElapsedTime(elapsedMs).replace(/^用时\s*/u, '');
}

function dateLabel(value: string): string {
  return new Date(value).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/** Presentation only: calculations and ranking stay in the existing domain helpers. */
export function HomeworkPerformanceTable({
  label,
  identityHeading,
  rows,
}: {
  readonly label: string;
  readonly identityHeading: string;
  readonly rows: readonly {
    readonly identity: ReactNode;
    readonly performance: HomeworkAssignmentPerformance;
  }[];
}) {
  return (
    <div className="teacher-data__table-wrap">
      <table className="teacher-data__table teacher-data__table--performance" aria-label={label}>
        <thead>
          <tr>
            <th scope="col">{identityHeading}</th>
            <th scope="col">正确</th>
            <th scope="col">错误</th>
            <th scope="col">待批改</th>
            <th scope="col">未作答</th>
            <th scope="col">正确率</th>
            <th scope="col">总用时</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ identity, performance }) => (
            <tr key={performance.assignment.assignmentId}>
              <th scope="row">{identity}</th>
              <td data-label="正确">
                <span className="teacher-data__number teacher-data__number--correct">
                  {performance.correct}
                </span>{' '}
                题
              </td>
              <td data-label="错误">
                <span className="teacher-data__number">{performance.incorrect}</span> 题
              </td>
              <td data-label="待批改">{performance.pending} 题</td>
              <td data-label="未作答">{performance.unanswered} 题</td>
              <td data-label="正确率">
                <strong className="teacher-data__accuracy">
                  {performance.accuracy.toFixed(1)}%
                </strong>
              </td>
              <td data-label="总用时">{elapsedLabel(performance.totalElapsedMs)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function HomeworkAnalyticsPanel({
  mode,
  homework,
  teaching,
  onCancel,
}: {
  readonly mode: HomeworkAnalyticsMode;
  readonly homework: HomeworkStore;
  readonly teaching: TeachingStore;
  readonly onCancel: () => void;
}) {
  const homeworkState = useSyncExternalStore(
    homework.subscribe,
    homework.snapshot,
    homework.snapshot,
  );
  const teachingState = useSyncExternalStore(
    teaching.subscribe,
    teaching.snapshot,
    teaching.snapshot,
  );
  const [selectedHomeworkId, setSelectedHomeworkId] = useState<string | null>(null);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [classSearch, setClassSearch] = useState('');

  useEffect(() => {
    let active = true;
    void teaching
      .loadTeacherDashboard()
      .then(() => {
        if (!active) return;
        return homework.loadTeacherHomeworkDataset(
          teaching.snapshot().classes.map((managedClass) => managedClass.classId),
        );
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [homework, teaching]);

  const studentNames = useMemo(
    () =>
      new Map(
        teachingState.students.map(
          (student) => [student.membershipId, student.user.displayName] as const,
        ),
      ),
    [teachingState.students],
  );
  const classNames = useMemo(
    () =>
      new Map(
        teachingState.classes.map(
          (managedClass) => [managedClass.classId, managedClass.name] as const,
        ),
      ),
    [teachingState.classes],
  );
  const homeworkById = useMemo(
    () => new Map(homeworkState.homeworks.map((item) => [item.homeworkId, item] as const)),
    [homeworkState.homeworks],
  );
  const allAssignments = useMemo(
    () =>
      homeworkState.homeworks.flatMap(
        (item) => homeworkState.reviewAssignmentsByHomework[item.homeworkId] ?? [],
      ),
    [homeworkState.homeworks, homeworkState.reviewAssignmentsByHomework],
  );
  const filteredAssignments = useMemo(
    () => filterHomeworkAssignmentsByClass(allAssignments, classNames, classSearch),
    [allAssignments, classNames, classSearch],
  );
  const studentIds = useMemo(
    () => [...new Set(filteredAssignments.map((assignment) => assignment.studentMembershipId))],
    [filteredAssignments],
  );

  useEffect(() => {
    if (mode !== 'HOMEWORK_STUDENT_RESULTS') return;
    if (studentIds.includes(selectedStudentId)) return;
    setSelectedStudentId(studentIds[0] ?? '');
  }, [mode, selectedStudentId, studentIds]);

  const selectedHomework =
    selectedHomeworkId === null ? null : (homeworkById.get(selectedHomeworkId) ?? null);
  const rankedRows = useMemo(
    () =>
      selectedHomeworkId === null
        ? []
        : rankHomeworkAssignmentPerformances(
            homeworkState.reviewAssignmentsByHomework[selectedHomeworkId] ?? [],
          ),
    [homeworkState.reviewAssignmentsByHomework, selectedHomeworkId],
  );
  const studentRows = useMemo(
    () =>
      filteredAssignments
        .filter((assignment) => assignment.studentMembershipId === selectedStudentId)
        .map((assignment) => ({
          homework: homeworkById.get(assignment.homeworkId),
          performance: summarizeHomeworkAssignmentPerformance(assignment),
        }))
        .filter((row) => row.homework !== undefined)
        .sort((left, right) => right.homework!.createdAt.localeCompare(left.homework!.createdAt)),
    [filteredAssignments, homeworkById, selectedStudentId],
  );
  const loading =
    teachingState.pending === 'load-teacher' || homeworkState.pending === 'load-homework-data';
  const refresh = () => {
    void teaching
      .loadTeacherDashboard()
      .then(() =>
        homework.loadTeacherHomeworkDataset(
          teaching.snapshot().classes.map((managedClass) => managedClass.classId),
        ),
      )
      .catch(() => undefined);
  };
  const studentName = studentNames.get(selectedStudentId) ?? `学生 ${selectedStudentId.slice(-4)}`;

  return (
    <div className="teacher-data teacher-data--analytics">
      <div className="teacher-data__toolbar">
        <div className="teacher-data__intro-copy">
          <p>
            {mode === 'HOMEWORK_DATA'
              ? '按作业查看学生作答与批改数据。'
              : '选择学生，查看历次作业的作答表现。'}
          </p>
          <span>
            {loading
              ? '正在汇总数据…'
              : mode === 'HOMEWORK_DATA'
                ? `当前负责班级 · ${homeworkState.homeworks.length} 份作业`
                : `${studentIds.length} 名学生有作业记录`}
          </span>
        </div>
        <button
          className="teacher-data__button teacher-data__button--primary"
          type="button"
          disabled={loading}
          onClick={refresh}
        >
          {loading ? '刷新中…' : '刷新数据'}
        </button>
      </div>
      {teachingState.error !== null ? (
        <p className="message message--error" role="alert">
          {teachingState.error}
        </p>
      ) : null}
      {homeworkState.error !== null ? (
        <p className="message message--error" role="alert">
          {homeworkState.error}
        </p>
      ) : null}
      {loading ? (
        <div className="teacher-data__empty" role="status">
          <strong>正在汇总作业数据…</strong>
          <span>学生和作业较多时需要一点时间。</span>
        </div>
      ) : mode === 'HOMEWORK_DATA' ? (
        selectedHomework === null ? (
          <div>
            {homeworkState.homeworks.length > 0 ? (
              <div className="teacher-data__table-wrap">
                <table
                  className="teacher-data__table teacher-data__table--homeworks"
                  aria-label="已布置作业列表"
                >
                  <thead>
                    <tr>
                      <th scope="col">作业 / 班级</th>
                      <th scope="col">布置日期</th>
                      <th scope="col">题目数</th>
                      <th scope="col">学生数</th>
                      <th scope="col">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {homeworkState.homeworks.map((item) => (
                      <tr key={item.homeworkId}>
                        <th scope="row">
                          <span className="teacher-data__cell-stack">
                            <strong>{item.title}</strong>
                            <small>{classNames.get(item.classId) ?? '负责班级'}</small>
                          </span>
                        </th>
                        <td data-label="布置日期">
                          {dateLabel(item.publishedAt ?? item.createdAt)}
                        </td>
                        <td data-label="题目数">{item.items.length} 题</td>
                        <td data-label="学生数">
                          {homeworkState.reviewAssignmentsByHomework[item.homeworkId]?.length ??
                            '—'}
                          {homeworkState.reviewAssignmentsByHomework[item.homeworkId] === undefined
                            ? ''
                            : ' 人'}
                        </td>
                        <td className="teacher-data__actions-cell">
                          <button
                            className="teacher-data__button teacher-data__button--primary"
                            type="button"
                            aria-label={`查看数据：${item.title}`}
                            onClick={() => setSelectedHomeworkId(item.homeworkId)}
                          >
                            查看数据 <span aria-hidden="true">→</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="teacher-data__empty" role="status">
                <strong>暂无已布置作业</strong>
                <span>布置作业后，可在这里查看学生作答数据。</span>
              </div>
            )}
          </div>
        ) : (
          <div className="teacher-data__detail">
            <header className="teacher-data__detail-heading">
              <button
                className="teacher-data__button"
                type="button"
                onClick={() => setSelectedHomeworkId(null)}
              >
                ← 作业列表
              </button>
              <div>
                <h3>{selectedHomework.title}</h3>
                <p>
                  {classNames.get(selectedHomework.classId) ?? '负责班级'} ·{' '}
                  {selectedHomework.items.length} 题 · {rankedRows.length} 名学生
                </p>
              </div>
              <span className="teacher-data__count">按正确率从高到低</span>
            </header>
            {rankedRows.length > 0 ? (
              <HomeworkPerformanceTable
                label="学生作答数据"
                identityHeading="排名 / 学生"
                rows={rankedRows.map((performance, index) => ({
                  performance,
                  identity: (
                    <span className="teacher-data__ranked-name">
                      <span className="teacher-data__rank">{index + 1}</span>
                      <strong>
                        {studentNames.get(performance.assignment.studentMembershipId) ??
                          `学生 ${performance.assignment.studentMembershipId.slice(-4)}`}
                      </strong>
                    </span>
                  ),
                }))}
              />
            ) : (
              <div className="teacher-data__empty" role="status">
                <strong>暂无学生作答记录</strong>
                <span>有作业记录后将在这里显示。</span>
              </div>
            )}
            <p className="teacher-data__footnote">
              正确率按“正确题数 ÷ 作业总题数”计算；待批改和未作答单独列出，成绩随批改结果更新。
            </p>
          </div>
        )
      ) : (
        <div className="teacher-data__detail">
          <div className="teacher-data__student-filter">
            <label>
              班级
              <input
                type="search"
                aria-label="搜索班级"
                placeholder="输入班级名称"
                value={classSearch}
                onChange={(event) => setClassSearch(event.target.value)}
              />
            </label>
            <label>
              选择学生
              <select
                aria-label="选择学生"
                value={selectedStudentId}
                disabled={studentIds.length === 0}
                onChange={(event) => setSelectedStudentId(event.target.value)}
              >
                {studentIds.length === 0 ? <option value="">暂无有作业记录的学生</option> : null}
                {studentIds.map((membershipId) => (
                  <option key={membershipId} value={membershipId}>
                    {studentNames.get(membershipId) ?? `学生 ${membershipId.slice(-4)}`}
                  </option>
                ))}
              </select>
            </label>
            <span>仅显示当前负责班级中有作业记录的学生</span>
          </div>
          {selectedStudentId !== '' ? (
            <div className="teacher-data__student-heading">
              <span className="teacher-data__avatar" aria-hidden="true">
                {studentName.slice(0, 1)}
              </span>
              <div>
                <h3>{studentName}</h3>
                <p>历次作业成绩 · {studentRows.length} 份记录 · 按作业时间由近到远</p>
              </div>
            </div>
          ) : null}
          {studentRows.length > 0 ? (
            <HomeworkPerformanceTable
              label="学生所有作业成绩"
              identityHeading="作业 / 班级"
              rows={studentRows.map(({ homework: item, performance }) => ({
                performance,
                identity: (
                  <span className="teacher-data__cell-stack">
                    <strong>{item!.title}</strong>
                    <small>
                      {classNames.get(item!.classId) ?? '负责班级'} · {dateLabel(item!.createdAt)}
                    </small>
                  </span>
                ),
              }))}
            />
          ) : null}
          {studentRows.length === 0 ? (
            <div className="teacher-data__empty" role="status">
              <strong>
                {classSearch.trim() !== '' && studentIds.length === 0
                  ? '未找到该班级的学生作业记录'
                  : '该学生暂无作业数据'}
              </strong>
              <span>有作业记录后可在这里查看正确率与用时。</span>
            </div>
          ) : null}
          {studentRows.length > 0 ? (
            <p className="teacher-data__footnote">
              正确率按“正确题数 ÷ 作业总题数”计算；待批改和未作答单独列出，成绩随批改结果更新。
            </p>
          ) : null}
        </div>
      )}
      <footer className="teacher-data__footer">
        <button className="teacher-data__button return-button" type="button" onClick={onCancel}>
          返回作业管理
        </button>
        <span>数据来自已布置的作业与现有作答记录</span>
      </footer>
    </div>
  );
}
